import {computed, ComputedRef, ref} from "vue";
import {ToastServiceMethods} from "primevue/toastservice";
import {DocumentTable, DocumentTableColumn} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import {TableColumnWidthV2} from "@/util/TableSettings";
import {getColumnDataType} from "@/util/ValidationTableCellFormat";
import {ParsingHelper} from "@/util/ParsingHelper";
import {ValidationColumnWidthStore} from "@/util/ValidationColumnWidthStore";

type ExcelEditorSetting = {
	fields?: {name?: string; width?: string}[];
};

type Options = {
	table: DocumentTable;
	getDocumentClassId: () => number;
	visibleColumns: ComputedRef<DocumentTableColumn[]>;
	getVisibleColumn: (columnName?: string) => DocumentTableColumn | undefined;
	toast: ToastServiceMethods;
	t: (key: string) => string;
};

const DEFAULT_WIDTH = "160px";
const LAST_COLUMN_WIDTH = "220px";
const AMOUNT_WIDTH = "120px";

/** Stored widths are rem, the editor renders px. */
const toEditorWidth = (remWidth: string) => `${Math.round(ParsingHelper.remToPx(remWidth))}px`;

/** Column widths for the excel editor. Loading/saving is shared with the V1 table via ValidationColumnWidthStore. */
export function useValidationColumnWidths(options: Options) {
	const {table, getDocumentClassId, visibleColumns, getVisibleColumn, toast, t} = options;

	// saved widths in px (what the editor renders); persisted as rem
	const savedWidths = ref<TableColumnWidthV2[]>([]);
	const hasUserResizedColumn = ref(false);
	const widthsVersion = ref(0);

	const widthsByFieldId = computed(() => ValidationColumnWidthStore.toWidthMap(savedWidths.value));

	const getColumnWidth = (column: DocumentTableColumn, columnIndex: number) => {
		if (column.id && widthsByFieldId.value.has(column.id)) {
			return widthsByFieldId.value.get(column.id) as string;
		}
		if (getColumnDataType(column) === "amount") {
			return AMOUNT_WIDTH;
		}
		if (column.width) {
			return `${column.width}px`;
		}
		return columnIndex === visibleColumns.value.length - 1 ? LAST_COLUMN_WIDTH : DEFAULT_WIDTH;
	};

	const loadColumnWidths = async () => {
		if (!table.id) {
			return;
		}
		try {
			const cached = ValidationColumnWidthStore.readCache(table.id);
			const widths = cached ?? await ValidationColumnWidthStore.fetchFromApi(getDocumentClassId(), table.id);
			savedWidths.value = widths.map(width => ({tableFieldId: width.tableFieldId, width: toEditorWidth(width.width)}));
			widthsVersion.value++;
			if (!cached) {
				ValidationColumnWidthStore.writeCache(table.id, widths);
			}
		} catch (response: any) {
			ToastManager.showError(toast, t("Squeeze.General.Error"), response);
		}
	};

	const saveColumnWidth = async (columnId: number, widthInPx: string) => {
		if (!table.id) {
			return;
		}
		try {
			const widthExists = savedWidths.value.some(width => width.tableFieldId === columnId);
			await ValidationColumnWidthStore.persistWidth(getDocumentClassId(), table.id, columnId, String(ParsingHelper.pxToRem(widthInPx)), widthExists);
			const saved = savedWidths.value.find(width => width.tableFieldId === columnId);
			if (saved) {
				saved.width = widthInPx;
			} else {
				savedWidths.value.push({tableFieldId: columnId, width: widthInPx});
			}
			ValidationColumnWidthStore.writeCache(table.id, savedWidths.value.map(width => ({tableFieldId: width.tableFieldId, width: String(ParsingHelper.pxToRem(width.width))})));
		} catch (response: any) {
			ToastManager.showError(toast, t("Squeeze.General.Error"), response);
		}
	};

	const resetColumnWidths = async () => {
		if (!table.id) {
			return;
		}

		const tableId = table.id;
		const requests = (table.columns ?? []).map((column) => {
			if (!column.id) {
				return Promise.resolve();
			}
			return ValidationColumnWidthStore.clearWidth(getDocumentClassId(), tableId, column.id)
				.catch((response: string | Response) => ToastManager.showError(toast, t("Squeeze.General.Error"), response));
		});

		await Promise.all(requests);
		savedWidths.value = [];
		hasUserResizedColumn.value = false;
		widthsVersion.value++;
		ValidationColumnWidthStore.clearCache(tableId);
	};

	// the editor has no resize event, so we flag a drag on the ".col-sep" handle
	const onEditorMouseDown = (event: MouseEvent) => {
		if (event.target instanceof HTMLElement && event.target.classList.contains("col-sep")) {
			hasUserResizedColumn.value = true;
		}
	};

	const persistChangedWidths = async (setting: ExcelEditorSetting) => {
		// the last visible column auto-fills the viewport, so its width is never saved
		const autoFillColumn = visibleColumns.value[visibleColumns.value.length - 1];
		for (const field of setting.fields ?? []) {
			const column = getVisibleColumn(field.name);
			if (!column?.id || !field.width?.endsWith("px") || column === autoFillColumn) {
				continue;
			}
			if (widthsByFieldId.value.get(column.id) !== field.width) {
				await saveColumnWidth(column.id, field.width);
			}
		}
	};

	// the editor fires "setting" twice per resize; chaining the runs avoids saving the same width twice
	let pendingSave: Promise<void> = Promise.resolve();
	const onEditorSetting = (setting: ExcelEditorSetting) => {
		if (hasUserResizedColumn.value) {
			pendingSave = pendingSave.then(() => persistChangedWidths(setting));
		}
	};

	return {
		hasUserResizedColumn,
		widthsVersion,
		getColumnWidth,
		loadColumnWidths,
		resetColumnWidths,
		onEditorMouseDown,
		onEditorSetting,
	};
}
